import { Component } from '@theme/component';
import {
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  onDocumentReady,
} from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { ThemeEvents } from '@theme/events';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import {
  DialogCloseEvent,
  DialogComponent,
} from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources
 * available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput
 * @property {HTMLElement} predictiveSearchResults
 * @property {HTMLElement} resetButton
 * @property {HTMLElement[]} [resultsItems]
 * @property {HTMLElement} [recentlyViewedWrapper]
 * @property {HTMLElement[]} [recentlyViewedTitle]
 * @property {HTMLElement[]} [recentlyViewedItems]
 *
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = [
    'searchInput',
    'predictiveSearchResults',
    'resetButton',
  ];

  #controller = new AbortController();

  /** Maximum number of recently viewed products shown. */
  #recentlyViewedLimit = 3;

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {number[]} */
  #scrollResetTimers = [];

  #resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      this.style.setProperty(
        '--predictive-search-results-height',
        `${entry.contentRect.height}px`
      );
    }
  });

  /**
   * Get the enclosing dialog component.
   *
   * @returns {DialogComponent | null}
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener(
        'keydown',
        this.#handleKeyboardShortcut,
        { signal }
      );

      dialog.addEventListener(
        DialogCloseEvent.eventName,
        this.#handleDialogClose,
        { signal }
      );

      this.addEventListener(
        'click',
        this.#handleModalClick,
        { signal }
      );

      this.addEventListener(
        'pointerdown',
        this.#handlePointerInteraction,
        { signal }
      );

      this.addEventListener(
        'pointerup',
        this.#handlePointerRelease,
        { signal }
      );
    } else {
      document.addEventListener(
        ThemeEvents.megaMenuHover,
        this.#blurSearch,
        { signal }
      );
    }

    onDocumentReady(() => {
      if (this.refs.searchInput.value.trim().length === 0) {
        this.#getRecentlyViewed();
      }
    });

    const results =
      this.refs.predictiveSearchResults.firstElementChild;

    if (results) {
      this.#resizeObserver.observe(results);
    }

    this.#normaliseRenderedResults();
    this.#restorePreUpgradeState();
  }

  #restorePreUpgradeState() {
    const { searchInput } = this.refs;
    const searchTerm = searchInput.value.trim();

    if (document.activeElement === searchInput) {
      this.expandSearch();
    }

    if (!searchTerm.length) return;

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }

  /**
   * Keep focus on the search input when a non-interactive area
   * inside the modal is selected.
   *
   * @param {MouseEvent} event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (
      event.target
    );

    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (
      !isInteractiveElement &&
      this.refs.searchInput
    ) {
      this.refs.searchInput.focus();
    }
  };

  /**
   * Clear keyboard-only selection styling when pointer
   * or touch input is used.
   */
  #handlePointerInteraction = (event) => {
    this.#isKeyboardNavigation = false;
    this.#clearPointerStates();

    if (event.pointerType === 'touch' && event.target instanceof Element) {
      const touchedLink = event.target.closest(
        '.predictive-search-results__pill, .predictive-search-results__card a'
      );

      window.setTimeout(() => touchedLink?.blur(), 0);
    }
  };

  /**
   * Remove browser-retained touch focus after the tap animation completes.
   * @param {PointerEvent} event
   */
  #handlePointerRelease = (event) => {
    if (event.pointerType !== 'touch') return;

    const target = event.target instanceof Element ? event.target : null;
    const touchedLink = target?.closest(
      '.predictive-search-results__pill, .predictive-search-results__card a'
    );

    window.setTimeout(() => {
      touchedLink?.blur();
      this.#clearPointerStates();
    }, 80);
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#resizeObserver.disconnect();

    this.#scrollResetTimers.forEach((timer) => {
      window.clearTimeout(timer);
    });

    this.#scrollResetTimers = [];
  }

  /**
   * Handle the Command + K keyboard shortcut.
   *
   * @param {KeyboardEvent} event
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /** Keep combobox expansion state in sync with the visible popup. */
  #setExpanded(expanded) {
    this.refs.searchInput.setAttribute('aria-expanded', String(expanded));

    if (!expanded) {
      this.refs.searchInput.removeAttribute('aria-activedescendant');
    }
  }

  /** Reset the search when the dialog closes. */
  #handleDialogClose = () => {
    this.#setExpanded(false);
    this.#resetSearch();
  };

  expandSearch = () => {
    this.classList.add(
      'predictive-search--expanded'
    );
    this.#setExpanded(true);

    if (this.dataset.activeColorScheme) {
      const target = this.dialog ?? this;

      target.classList.add(
        `color-${this.dataset.activeColorScheme}`
      );
    }
  };

  get #allResultsItems() {
    return /** @type {HTMLElement[]} */ (
      Array.from(
        this.refs.predictiveSearchResults.querySelectorAll(
          '[ref="resultsItems[]"], [ref="recentlyViewedItems[]"]'
        )
      ).filter((item) => item instanceof HTMLElement)
    );
  }

  /** Whether the latest result interaction used a keyboard. */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return (
      this.#allResultsItems?.findIndex(
        (item) =>
          item.getAttribute('aria-selected') ===
          'true'
      ) ?? -1
    );
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    let activeOptionId = '';

    for (const [itemIndex, item] of
      this.#allResultsItems.entries()) {
      const active = itemIndex === index;
      item.setAttribute('aria-selected', String(active));

      if (active) {
        activeOptionId = item.id;

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }

        item.scrollIntoView({
          behavior: prefersReducedMotion()
            ? 'instant'
            : 'smooth',
          block: 'nearest',
        });
      }
    }

    if (activeOptionId) {
      this.refs.searchInput.setAttribute('aria-activedescendant', activeOptionId);
    } else {
      this.refs.searchInput.removeAttribute('aria-activedescendant');
    }

    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[
      this.#currentIndex
    ];
  }

  /**
   * Navigate through search results with the keyboard.
   *
   * @param {KeyboardEvent} event
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (
      !this.#allResultsItems?.length ||
      event.key === 'ArrowLeft' ||
      event.key === 'ArrowRight'
    ) {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems =
      this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();

        this.#currentIndex =
          currentIndex < totalItems - 1
            ? currentIndex + 1
            : 0;
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();

        this.#currentIndex =
          currentIndex > 0
            ? currentIndex - 1
            : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer =
          this.refs.predictiveSearchResults.querySelector(
            '[data-single-result-url]'
          );

        if (
          singleResultContainer instanceof
            HTMLElement &&
          singleResultContainer.dataset
            .singleResultUrl
        ) {
          event.preventDefault();

          window.location.href =
            singleResultContainer.dataset
              .singleResultUrl;

          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();

          this.#currentItem
            ?.querySelector('a')
            ?.click();
        } else {
          const searchUrl = new URL(
            Theme.routes.search_url,
            location.origin
          );

          searchUrl.searchParams.set(
            'q',
            this.refs.searchInput.value
          );

          window.location.href =
            searchUrl.toString();
        }

        break;
      }
    }
  };

  /**
   * Clear the recently viewed products.
   *
   * @param {Event} event
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const {
      recentlyViewedItems,
      recentlyViewedTitle,
      recentlyViewedWrapper,
    } = this.refs;

    const allRecentlyViewedElements = [
      ...(recentlyViewedItems || []),
      ...(recentlyViewedTitle || []),
    ];

    if (
      allRecentlyViewedElements.length === 0
    ) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add(
        'removing'
      );

      onAnimationEnd(
        recentlyViewedWrapper,
        () => {
          recentlyViewedWrapper.remove();
          this.#resetSearch();
        }
      );
    }
  }

  /**
   * Reset the search state.
   *
   * @param {boolean} [keepFocus=true]
   */
  resetSearch = debounce(
    (keepFocus = true) => {
      if (keepFocus) {
        this.refs.searchInput.focus();
      }

      this.#resetSearch();
    },
    100
  );

  /**
   * Fetch and display search results from the input.
   */
  search = debounce((event) => {
    if (!event.inputType) return;

    const searchTerm =
      this.refs.searchInput.value.trim();

    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Reset the results viewport and every native horizontal rail.
   * The rails no longer depend on Horizon's slideshow component, so
   * their first item can be restored deterministically after each morph.
   */
  #resetScrollPositions() {
    this.#scrollResetTimers.forEach((timer) => {
      window.clearTimeout(timer);
    });

    this.#scrollResetTimers = [];

    const reset = () => {
      const resultsInner =
        this.refs.predictiveSearchResults.querySelector(
          '.predictive-search-results__inner'
        );

      if (resultsInner instanceof HTMLElement) {
        resultsInner.scrollTop = 0;
      }

      const formContent = this.querySelector(
        '.predictive-search-form__content'
      );

      if (formContent instanceof HTMLElement) {
        formContent.scrollTop = 0;
      }

      this.refs.predictiveSearchResults
        .querySelectorAll('[data-predictive-rail]')
        .forEach((rail) => {
          if (!(rail instanceof HTMLElement)) return;

          const previousSnapType = rail.style.scrollSnapType;
          const previousBehavior = rail.style.scrollBehavior;

          rail.style.scrollSnapType = 'none';
          rail.style.scrollBehavior = 'auto';
          rail.scrollLeft = 0;

          rail.scrollTo({
            left: 0,
            top: 0,
            behavior: 'auto',
          });
          rail.scrollLeft = 0;
          void rail.offsetWidth;

          rail.style.scrollSnapType = previousSnapType;
          rail.style.scrollBehavior = previousBehavior;
        });
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(reset);
    });

    this.#scrollResetTimers.push(
      window.setTimeout(reset, 80),
      window.setTimeout(reset, 180)
    );
  }

  /** Remove duplicate suggested products already shown as recently viewed. */
  #dedupeEmptyStateProducts() {
    const container = this.refs.predictiveSearchResults;
    const recentGroup = container.querySelector(
      '[data-product-group="recently-viewed"]'
    );
    const suggestedGroup = container.querySelector(
      '[data-product-group="suggested"]'
    );

    if (!(recentGroup instanceof HTMLElement)) return;
    if (!(suggestedGroup instanceof HTMLElement)) return;

    const recentUrls = new Set(
      Array.from(recentGroup.querySelectorAll('[data-product-url]'))
        .map((item) => item.getAttribute('data-product-url'))
        .filter(Boolean)
    );

    suggestedGroup
      .querySelectorAll('[data-product-url]')
      .forEach((item) => {
        const productUrl = item.getAttribute('data-product-url');

        if (productUrl && recentUrls.has(productUrl)) {
          item.remove();
        }
      });

    if (!suggestedGroup.querySelector('[data-product-url]')) {
      suggestedGroup.hidden = true;
    }
  }

  /** Clear pointer-only states that can otherwise look selected on touchscreens. */
  #clearPointerStates() {
    if (!window.matchMedia('(hover: none), (pointer: coarse)').matches) {
      return;
    }

    this.refs.predictiveSearchResults
      .querySelectorAll('[aria-selected="true"], .keyboard-focus')
      .forEach((item) => {
        if (item.getAttribute('role') === 'option') {
          item.setAttribute('aria-selected', 'false');
        } else {
          item.removeAttribute('aria-selected');
        }
        item.classList.remove('keyboard-focus');
      });

    this.refs.searchInput.removeAttribute('aria-activedescendant');

    const activeElement = document.activeElement;
    if (
      activeElement instanceof HTMLElement &&
      activeElement !== this.refs.searchInput &&
      this.refs.predictiveSearchResults.contains(activeElement)
    ) {
      activeElement.blur();
    }
  }

  /** Apply all post-render normalisation in one predictable sequence. */
  #normaliseRenderedResults() {
    this.#dedupeEmptyStateProducts();
    this.#clearPointerStates();
    this.#resetScrollPositions();
  }

  /**
   * Fetch predictive search results.
   *
   * @param {string} searchTerm
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(
      Theme.routes.predictive_search_url,
      location.origin
    );

    url.searchParams.set('q', searchTerm);

    url.searchParams.set(
      'resources[limit_scope]',
      'each'
    );

    const { predictiveSearchResults } =
      this.refs;

    const abortController =
      this.#createAbortController();

    sectionRenderer
      .getSectionHTML(
        this.dataset.sectionId,
        false,
        url
      )
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;
        if (abortController.signal.aborted) {
          return;
        }

        morph(
          predictiveSearchResults,
          resultsMarkup
        );

        this.#normaliseRenderedResults();
      })
      .catch((error) => {
        if (abortController.signal.aborted) {
          return;
        }

        throw error;
      });
  }

  /**
   * Fetch recently viewed product markup.
   *
   * @returns {Promise<string | null>}
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) {
      return null;
    }

    const viewedProducts = RecentlyViewed
      .getProducts()
      .slice(0, this.#recentlyViewedLimit);

    if (viewedProducts.length === 0) {
      return null;
    }

    const url = new URL(
      Theme.routes.search_url,
      location.origin
    );

    url.searchParams.set(
      'q',
      viewedProducts
        .map(
          /** @param {string} id */
          (id) => `id:${id}`
        )
        .join(' OR ')
    );

    url.searchParams.set(
      'resources[type]',
      'product'
    );

    return sectionRenderer.getSectionHTML(
      this.dataset.sectionId,
      false,
      url
    );
  }

  /** Fetch and insert recently viewed products. */
  #getRecentlyViewed = async () => {
    const { predictiveSearchResults } =
      this.refs;

    const abortController =
      this.#createAbortController();

    try {
      const resultsMarkup =
        await this.#getRecentlyViewedProductsMarkup();

      if (!resultsMarkup) return;

      const parsedNextPage =
        new DOMParser().parseFromString(
          resultsMarkup,
          'text/html'
        );

      const recentlyViewedProductsHtml =
        parsedNextPage.getElementById(
          'predictive-search-products'
        );

      if (!recentlyViewedProductsHtml) {
        return;
      }

      for (const child of
        recentlyViewedProductsHtml.children) {
        if (
          child instanceof HTMLElement
        ) {
          child.setAttribute(
            'ref',
            'recentlyViewedWrapper'
          );
        }
      }

      const collectionElement =
        predictiveSearchResults.querySelector(
          '#predictive-search-products'
        );

      if (!collectionElement) return;

      if (
        this.refs.recentlyViewedWrapper
      ) {
        this.refs.recentlyViewedWrapper.remove();
      }

      if (abortController.signal.aborted) {
        return;
      }

      collectionElement.prepend(
        ...recentlyViewedProductsHtml.children
      );

      this.#normaliseRenderedResults();
    } catch (error) {
      throw error;
    }
  };

  #hideResetButton() {
    this.refs.resetButton.hidden = true;
  }

  #showResetButton() {
    this.refs.resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController =
      new AbortController();

    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    this.#activeFetch = abortController;

    return abortController;
  }

  /** Restore the empty-search state. */
  #resetSearch = async () => {
    const {
      predictiveSearchResults,
      searchInput,
    } = this.refs;

    const emptySectionId =
      'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController =
      this.#createAbortController();

    const emptySectionMarkup =
      await sectionRenderer.getSectionHTML(
        emptySectionId,
        false
      );

    const parsedEmptySectionMarkup =
      new DOMParser()
        .parseFromString(
          emptySectionMarkup,
          'text/html'
        )
        .querySelector(
          '.predictive-search-empty-section'
        );

    if (!parsedEmptySectionMarkup) {
      throw new Error(
        'No empty section markup found'
      );
    }

    const viewedProducts = RecentlyViewed
      .getProducts()
      .slice(0, this.#recentlyViewedLimit);

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup =
        await this.#getRecentlyViewedProductsMarkup();

      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup =
        new DOMParser().parseFromString(
          recentlyViewedMarkup,
          'text/html'
        );

      const recentlyViewedProductsHtml =
        parsedRecentlyViewedMarkup.getElementById(
          'predictive-search-products'
        );

      if (!recentlyViewedProductsHtml) {
        return;
      }

      for (const child of
        recentlyViewedProductsHtml.children) {
        if (
          child instanceof HTMLElement
        ) {
          child.setAttribute(
            'ref',
            'recentlyViewedWrapper'
          );
        }
      }

      const collectionElement =
        parsedEmptySectionMarkup.querySelector(
          '#predictive-search-products'
        );

      if (!collectionElement) return;

      collectionElement.prepend(
        ...recentlyViewedProductsHtml.children
      );
    }

    if (abortController.signal.aborted) {
      return;
    }

    morph(
      predictiveSearchResults,
      parsedEmptySectionMarkup
    );

    this.#normaliseRenderedResults();
  };

  /** Remove focus from the search input and expose the popup as collapsed. */
  #blurSearch = () => {
    this.#setExpanded(false);
    this.refs.searchInput.blur();
  };
}

if (
  !customElements.get(
    'predictive-search-component'
  )
) {
  customElements.define(
    'predictive-search-component',
    PredictiveSearchComponent
  );
}